/**
 * O conteúdo do **modal do Negócio** (3.10; reestruturado na 11.4 — decisão 17 da proposta de
 * 23/09/2026, estrutura do Agendor).
 *
 * A ordem é a da espinha: **Perdido | Ganho** grandes no topo (Perdido abre o modal estreito da
 * 11.3 — motivo obrigatório, descrição opcional; Ganho marca à mão, FR-8), os **chevrons** das
 * etapas do funil atual (a atual em dourado; clicar move, para a frente ou para trás), o
 * **compositor** (Nota + um tipo de tarefa por aba), o **histórico de atividades** (notas e tarefas),
 * **Movimentações** (etapa/funil — AD-20 —, cartão próprio, fora do histórico), o **ID do negócio**
 * com Copiar, o **Orçamento espelho** (e os Produtos), os **Dados do negócio** (com os campos
 * personalizados da 11.3), a **Empresa**, os e-mails arquivados e a **RAT** (desenho do v1). **Não
 * há cartão "Ações"** (decisão do Gustavo): excluir o negócio é um botão discreto no rodapé.
 *
 * **Previsão de fechamento (11.8, FR-78)** é um campo dos Dados do negócio: um item da lista
 * administrada em Configuração → Negócios, **ou nenhum** — sem data, sem porcentagem. O seletor só
 * oferece os itens ativos; o item que a gestão excluiu depois continua rotulando o negócio que o
 * aponta (e aparece no seletor como o valor atual, marcado "excluído da lista"). A marca é o ponto
 * na cor do item com o rótulo ao lado e a descrição embaixo. Ela viaja no mesmo "Salvar dados"
 * (`PUT` de corpo completo) — por isso o rascunho a carrega sempre, e salvar os campos
 * personalizados nunca a desmarca. **Frete e Negociação saíram na 13.16** (itens J e K do teste de
 * 26/10/2026): da tela e do contrato; as colunas ficam no banco com o que tinham.
 *
 * **O aviso do Ganho sem orçamento (13.16, item K):** a janela "Marcar negócio como ganho" pede
 * confirmação em destaque quando o Negócio não tem Orçamento espelho vinculado (`com_orcamento`,
 * "sem orçamento") **ou** não tem nenhuma linha de Produto (`com_produtos`, "sem produtos") — sem
 * Produto, o ganho congela o valor em R$ 0,00. É aviso, e não trava: "Confirmar ganho" ganha como
 * sempre. Como o espelho chega por evento, a ficha é relida ao abrir a janela; "Confirmar ganho"
 * espera a releitura, e a releitura que falha não derruba a ficha.
 *
 * **Toda escrita reusa a rota que já existe** — mover (`PUT /etapa`, só dentro do funil),
 * ganhar/perder (`PUT /desfecho`), editar (`PUT /negocios/{id}`), excluir (`DELETE`), a nota —, e a
 * 11.5 acrescentou a **troca de Responsável** (`PUT /responsavel`): o campo Responsável dos Dados
 * do negócio é um seletor, e é ele o gatilho do **handoff** automático (FR-6). Não há botão de
 * handoff, nem "entregar para outro funil": num negócio de Prospecção, trocar o Responsável para um
 * Vendedor (marcado no seletor, e confirmado num diálogo) leva o negócio à primeira etapa de
 * Vendas, e um aviso diz para onde ele foi — na região de avisos do layout, que sobrevive ao modal
 * quando quem entregou deixa de enxergar o negócio (o `404` pós-troca fecha o modal).
 *
 * **Sigilo (FR-62):** a resposta a uma SDR não carrega `valor`; aqui o campo simplesmente não é
 * desenhado — nem traço (State Patterns). Os nomes (Empresa, equipe, rótulos) são compostos pelo
 * SPA com o JWT de quem olha (AD-25).
 *
 * Uma árvore só: o modal tem 680 px no computador e é tela cheia no celular (`ModalDoNegocio`); o
 * conteúdo é a mesma coluna nos dois — a paridade da NFR-1 é de função e de dado.
 */

import { Link } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import {
  useEmpresa,
  useNomesDePessoas,
  useOpcoesDeDominio,
  useRotulosDeOpcoes,
} from '@/modulos/cadastro/api'
import { nomeExibidoDaEmpresa, razaoSocialQueDifere } from '@/modulos/cadastro/nome-da-empresa'
import { EmailsDoNegocio } from '@/modulos/comunicacao/EmailsDoNegocio'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { ehDeGestao } from '@/shell/navegacao'
import { OrcamentoDoNegocio } from '@/modulos/integracao-erp/OrcamentoDoNegocio'
import { formatarTelefone } from '@/telefone/mascara-do-telefone'
import { ChevronsDeEtapa } from './ChevronsDeEtapa'
import { type ControleDoCompositor, CompositorDoNegocio } from './CompositorDoNegocio'
import { DefinicaoDaEmpresaDoNegocio, textoDaDefinicao } from './DefinicaoDaEmpresaDoNegocio'
import { Dialogo } from './Dialogo'
import {
  CONFERIR_DE_NOVO,
  CONTATO_AINDA_NAO_VINCULADO,
  CONTATO_VINCULADO_A_EMPRESA,
  VINCULANDO_O_CONTATO,
  useEsperaDoVinculo,
} from './espera-do-vinculo'
import { HistoricoDeAtividades } from './HistoricoDeAtividades'
import { IdDoNegocio } from './IdDoNegocio'
import { MarcaDePrevisao } from './MarcaDePrevisao'
import { OrcamentoDoCrm, textoDaTaxa } from './OrcamentoDoCrm'
import { ProdutosDoNegocio } from './ProdutosDoNegocio'
import { RatDoNegocio } from './RatDoNegocio'
import { TrocaDaEmpresaDoNegocio, textoDaTroca } from './TrocaDaEmpresaDoNegocio'
import { anunciar } from './avisos'
import { dias as diasPorExtenso } from './ReguaDeDias'
import {
  type Funil,
  type MovimentacaoDoNegocio,
  camposDoNegocio,
  useEditarNegocio,
  useExcluirNegocio,
  useFicha,
  useFunis,
  useItensDePrevisao,
  useMarcarDesfecho,
  useMovimentacoes,
  useMoverDeEtapa,
  useRegistrarEntrega,
  useTrocarResponsavel,
} from './api'
import {
  CONTATO_A_CAMINHO,
  DATA,
  DATA_E_HORA,
  DEFINA_A_EMPRESA_PARA_GANHAR,
  DEFINA_A_EMPRESA_PARA_MOVER,
  EMPRESA_A_DEFINIR,
  ROTULO_DE_HANDOFF,
  ROTULO_DO_STATUS,
  TAMANHO_DA_DESCRICAO_DA_PERDA,
  hojeEmSaoPaulo,
  partesDoPrazo,
  sinalDaCor,
  textoDoValor,
} from './apresentacao'
import './ficha-do-negocio.css'

/** O título do modal enquanto a Empresa não chega — e o nome da região. */
export const TITULO_DA_FICHA = 'Negócio'

/** O estado de Negócio inexistente/invisível da espinha (State Patterns). */
export const NEGOCIO_NAO_ENCONTRADO = 'Negócio não encontrado.'

const VAZIO = '—'
const NOME_A_CAMINHO = ' (carregando o nome da empresa)'
const EMPRESA_A_CAMINHO = 'Carregando a empresa…'
export const EMPRESA_INVISIVEL = 'A empresa deste negócio não está visível para você.'
const CARREGANDO = 'Carregando o negócio…'
const TENTAR_DE_NOVO = 'Tentar de novo'
export const NEGOCIO_FECHADO = 'Negócio ganho ou perdido não muda mais.'
/**
 * 13.16 (item K): o aviso da janela de Ganho quando o Negócio não tem Orçamento espelho vinculado —
 * o texto é o do Gustavo, exato. Não confundir com `SEM_ORCAMENTO`, que é o "sem valor" do
 * subtítulo.
 */
export const AVISO_DO_GANHO_SEM_ORCAMENTO =
  'O negócio está sem orçamento. Tem certeza que quer dar como Ganho?'
/** 13.16: o aviso quando há vínculo de Orçamento, mas nenhum Produto (o ganho congela R$ 0,00). */
export const AVISO_DO_GANHO_SEM_PRODUTOS =
  'O negócio está sem produtos. Tem certeza que quer dar como Ganho?'

/**
 * Qual aviso a janela de Ganho mostra, ou nenhum. Sem vínculo vale o de orçamento (mesmo sem
 * Produto); com vínculo e sem Produto, o de produtos. Só o `false` explícito avisa: a ficha de um
 * backend anterior à 13.16 não traz as chaves, e `undefined` não é "sem orçamento".
 */
export function avisoDoGanho(ficha: {
  com_orcamento?: boolean
  com_produtos?: boolean
}): string | null {
  if (ficha.com_orcamento === false) {
    return AVISO_DO_GANHO_SEM_ORCAMENTO
  }
  if (ficha.com_produtos === false) {
    return AVISO_DO_GANHO_SEM_PRODUTOS
  }
  return null
}
export const SEM_PERMISSAO_DE_EDITAR = 'Você não tem a permissão de editar negócio.'
/**
 * 13.14c (DW-527): quem vê o Negócio só pela tarefa aberta que tem nele (PA-3) o lê, registra nota e
 * tarefa, e não o altera — o serviço recusaria.
 */
export const SOMENTE_LEITURA_PELA_TAREFA =
  'Você vê este Negócio pela tarefa aberta que tem nele: pode registrar notas e tarefas, e só quem é responsável por ele ou a gestão o altera.'
/** O botão do cartão Empresa que abre a troca (13.5, FR-83) — só com a permissão da gestão. */
export const TROCAR_EMPRESA = 'Trocar empresa'
/**
 * O botão do cartão Empresa do Negócio sem Empresa (13.20b2): da Responsável (com `editar_negocio`)
 * e da gestão (com `trocar_empresa_do_negocio`), em qualquer status.
 */
export const DEFINIR_A_EMPRESA = 'Definir a empresa'
/** A linha do contato do Negócio sem Empresa no cartão Empresa (13.20b2). */
export const ROTULO_DO_CONTATO = 'Contato'
/** O nome da Empresa atual enquanto a ficha dela não chegou (ou a RLS a esconde, FR-30). */
const EMPRESA_ATUAL_SEM_NOME = 'a empresa atual'
export const OPCAO_FORA_DA_LISTA = 'valor atual (fora da lista)'
export const EQUIPE_INDISPONIVEL = 'nome indisponível'
const MOTIVOS_A_CAMINHO = 'Carregando os Motivos de perda…'
export const MOTIVOS_NAO_VIERAM =
  'Não foi possível carregar os Motivos de perda. Feche e tente de novo em instantes.'
export const ETAPAS_A_CAMINHO = 'Carregando as etapas do funil…'
export const FUNIL_FORA_DO_CATALOGO =
  'O funil deste negócio não está no catálogo lido. Feche e abra de novo para recarregar.'
const SEM_MOVIMENTACOES = 'Este negócio ainda não mudou de etapa.'
/**
 * Quantas movimentações o cartão mostra: as **últimas**, e só elas (decisão do Gustavo em
 * 26/09/2026, ao ver o cartão crescer sem fim). A trilha chega do serviço da mais recente para a
 * mais antiga, então o corte é o começo da lista; o rodapé diz que houve corte, e de quantas.
 */
export const LIMITE_DE_MOVIMENTACOES = 5
/**
 * A origem do valor (13.22b2): "do orçamento nº 12, com 5,00% de desconto" quando o valor do
 * Negócio é o total à vista da foto vigente — os dois campos vêm juntos, do mesmo serviço. Sem
 * eles (sem foto vigente, Negócio fechado ou a SDR), nada.
 */
export function fraseDaOrigemDoValor(
  numero: number | null | undefined,
  desconto: string | null | undefined,
): string | null {
  if (numero === null || numero === undefined || desconto === null || desconto === undefined) {
    return null
  }
  return `do orçamento nº ${String(numero)}, com ${textoDaTaxa(desconto)} de desconto`
}

export function corteDasMovimentacoes(mostradas: number, total: number): string {
  return `As ${String(mostradas)} últimas, de ${String(total)}`
}
export const SEM_MOTIVOS =
  'Nenhum Motivo de perda cadastrado. A gestão os cadastra em Configuração, e sem um deles o ' +
  'negócio não pode ser marcado como perdido.'
export const DADOS_GRAVADOS = 'Dados gravados.'

/** O toast do handoff (11.5): os nomes **reais** do funil e da etapa de destino, interpolados. */
export function textoDoHandoff(funil: string, etapa: string): string {
  return `Negócio entregue: agora em ${funil} · ${etapa}.`
}
/** O toast do handoff quando o catálogo ainda não tem o destino (raro: o catálogo é relido). */
export const HANDOFF_SEM_NOMES = 'Negócio entregue ao funil de Vendas.'
/** O aviso de saída da troca simples que tira o negócio do alcance de quem a fez. */
export function textoDaEntrega(nome: string): string {
  return `Negócio entregue a ${nome}.`
}
/** A marca, no seletor, das opções que disparam o handoff (a troca atravessa de funil). */
export const MARCA_DO_HANDOFF_NA_OPCAO = ' — leva a Vendas'
/** O corpo do diálogo de confirmação da troca que atravessa de funil. */
export function textoDaConfirmacao(nome: string): string {
  return (
    `Trocar o Responsável para ${nome} entrega o negócio ao funil de Vendas, na primeira etapa. ` +
    'Ele sai do funil de Prospecção e passa a aparecer em Originados.'
  )
}
const ID_DO_RESPONSAVEL = 'ficha-responsavel'

const ROTULOS = {
  responsavel: 'Responsável',
  sdr: 'SDR',
  origem: 'Origem do cliente',
  inicio: 'Data de início',
  conclusao: 'Data de conclusão',
  motivo: 'Motivo da perda',
  descricaoDaPerda: 'Descrição da perda',
  pedido: 'Nº do Pedido',
  entrega: 'Entregue em',
  previsao: 'Previsão de fechamento',
} as const

const ID_DA_ENTREGA = 'ficha-entregue-em'
const ID_DA_PREVISAO = 'ficha-previsao'

/** A opção "nenhuma" do seletor da Previsão — marcar é ato de quem edita, e não marcar vale. */
export const SEM_PREVISAO = 'Nenhuma'
/** O sufixo, no seletor, do item atual que a gestão excluiu depois (ele continua rotulando). */
export const MARCA_DE_PREVISAO_EXCLUIDA = ' (excluído da lista)'
/** O item marcado cujo rótulo não pôde ser lido (a leitura falhou) — nunca "sem previsão". */
export const PREVISAO_SEM_ROTULO = 'Previsão marcada (rótulo indisponível)'
/** O item marcado cujo rótulo ainda está vindo. */
export const PREVISAO_CARREGANDO = 'Carregando a previsão…'
/** A lista (ou o item marcado) não veio; o botão ao lado refaz a leitura. */
export const PREVISOES_NAO_VIERAM = 'Não foi possível carregar a Previsão de fechamento.'
const PISO_DA_ENTREGA = '2000-01-01'
const ID_DA_RAZAO = 'ficha-razao-do-bloqueio'
const ID_DA_SO_LEITURA = 'ficha-somente-leitura'
/* 13.20b2: as duas razões do Negócio sem Empresa — a dos chevrons e a do "Ganho". */
const ID_DA_RAZAO_DO_MOVER = 'ficha-razao-sem-empresa-mover'
const ID_DA_RAZAO_DO_GANHO = 'ficha-razao-sem-empresa-ganho'

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

function Linha({
  rotulo,
  para,
  children,
}: {
  rotulo: string
  para?: string
  children: React.ReactNode
}) {
  return (
    <div className="ficha__linha">
      {para === undefined ? (
        <span className="ficha__linha-rotulo">{rotulo}</span>
      ) : (
        <label className="ficha__linha-rotulo" htmlFor={para}>
          {rotulo}
        </label>
      )}
      {children}
    </div>
  )
}

function LinhaLida({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <Linha rotulo={rotulo}>
      <span className="ficha__linha-valor" data-vazio={valor === VAZIO}>
        {valor}
      </span>
    </Linha>
  )
}

/** A data (`AAAA-MM-DD`) como a tela a escreve, sem passar por fuso — a de entrega é um dia. */
function dataSemFuso(data: string | null): string {
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(data ?? '')
  return partes === null ? VAZIO : `${partes[3] ?? ''}/${partes[2] ?? ''}/${partes[1] ?? ''}`
}

/** A data de entrega do Negócio ganho (6.4, FR-74): grava no blur. */
function LinhaDaEntrega({ negocioId, entregueEm }: { negocioId: string; entregueEm: string | null }) {
  const registro = useRegistrarEntrega(negocioId)
  const [rascunho, setRascunho] = useState(entregueEm ?? '')
  const enviada = useRef<string | null>(null)
  return (
    <>
      <Linha rotulo={ROTULOS.entrega} para={ID_DA_ENTREGA}>
        <input
          id={ID_DA_ENTREGA}
          className="ficha__entrada"
          type="date"
          min={PISO_DA_ENTREGA}
          max={hojeEmSaoPaulo()}
          disabled={registro.isPending}
          value={rascunho}
          onChange={(evento) => {
            setRascunho(evento.target.value)
          }}
          onBlur={() => {
            /* Uma gravação por valor: um segundo blur antes de a ficha reler não reenvia. */
            if (rascunho === '' || rascunho === entregueEm || rascunho === enviada.current) {
              return
            }
            enviada.current = rascunho
            registro.mutate(rascunho, {
              onError: () => {
                enviada.current = null
              },
            })
          }}
        />
      </Linha>
      {registro.isError ? (
        <p className="ficha__erro" role="alert">
          {mensagemDe(registro.error)}
        </p>
      ) : null}
    </>
  )
}

/** Um campo personalizado como a tela o edita: sempre texto no controle. */
type RascunhoDosCampos = Record<string, string>

function valorNoControle(valor: string | number | undefined): string {
  return valor === undefined ? '' : String(valor)
}

export interface PropsDaFichaDoNegocio {
  negocioId: string
  /** Pede para fechar o modal — o botão × (o modal pergunta se houver alteração pendente). */
  aoFechar: () => void
  /** O Negócio foi excluído: fecha sem perguntar. */
  aoExcluido?: () => void
  /**
   * O Negócio foi entregue (troca de Responsável) a quem quem o entregou não enxerga mais — o
   * refetch da ficha deu `404`: fecha sem perguntar, e o aviso fica na região de fora (11.5).
   */
  aoEntregue?: () => void
  /** Avisa o modal quando há alteração digitada e não salva (para perguntar antes de fechar). */
  aoMudarPendencia?: (pendente: boolean) => void
  /** Aberto pelo endereço real (link colado): o botão "Ver no funil" leva ao quadro do Funil. */
  aoVerNoFunil?: (funilId: string) => void
}

export function FichaDoNegocio({
  negocioId,
  aoFechar,
  aoExcluido,
  aoEntregue,
  aoMudarPendencia,
  aoVerNoFunil,
}: PropsDaFichaDoNegocio) {
  const { data: sessao } = useSessao()
  const catalogo = useFunis()
  const funis = useMemo(() => catalogo.data ?? [], [catalogo.data])

  const mover = useMoverDeEtapa(negocioId)
  const desfecho = useMarcarDesfecho(negocioId)
  const editar = useEditarNegocio(negocioId)
  const excluir = useExcluirNegocio(negocioId)
  const trocarResponsavel = useTrocarResponsavel(negocioId)
  /* A troca feita: o aviso de saída que vale se a ficha deixar de ser visível (o `404` pós-troca).
     `null` = nenhuma troca feita nesta abertura. */
  const [avisoDeSaida, setAvisoDeSaida] = useState<string | null>(null)
  /* A troca que atravessa de funil espera confirmação (11.5): o id escolhido no seletor. */
  const [trocaAConfirmar, setTrocaAConfirmar] = useState<string | null>(null)
  /* Outra escrita deu certo: o erro antigo da troca (junto ao campo) já não descreve a ficha. */
  const outraEscritaDeuCerto = mover.isSuccess || desfecho.isSuccess || editar.isSuccess
  const limparATroca = trocarResponsavel.reset
  useEffect(() => {
    if (outraEscritaDeuCerto) {
      limparATroca()
    }
  }, [outraEscritaDeuCerto, limparATroca])

  /* Excluído o Negócio, as leituras dele são desligadas — senão o refetch recebe `404` e a tela
     desenharia a recusa no caminho de saída. */
  const foiExcluido = excluir.isSuccess
  /* Marcado **no clique** de confirmar, e não só pelo `isPending`: com o serviço rápido, o `onSuccess`
     (que invalida o prefixo) chega antes de o React desenhar o `isPending`, e as leituras ainda
     ligadas iriam buscar o Negócio que acabou de sumir. */
  const [confirmouAExclusao, setConfirmouAExclusao] = useState(false)
  const saindoPelaExclusao = confirmouAExclusao || excluir.isPending || foiExcluido
  const consulta = useFicha(negocioId, { habilitada: !foiExcluido })
  const ficha = consulta.data
  /* O `404` depois de uma troca (11.5): a SDR que entregou a um Vendedor, ou o Vendedor que
     entregou o próprio Negócio, deixou de enxergá-lo — é saída, e não "Negócio não encontrado.". */
  const saiuPelaTroca =
    avisoDeSaida !== null &&
    consulta.isError &&
    consulta.error instanceof ErroDaApi &&
    consulta.error.status === 404
  useEffect(() => {
    if (!saiuPelaTroca || avisoDeSaida === null) {
      return
    }
    anunciar(avisoDeSaida)
    ;(aoEntregue ?? aoFechar)()
  }, [saiuPelaTroca, avisoDeSaida, aoEntregue, aoFechar])
  const trilha = useMovimentacoes(negocioId, { habilitada: !foiExcluido })

  const empresa = useEmpresa(ficha?.empresa_id ?? undefined)
  /* 13.20b2: o Negócio sem Empresa é nomeado pelo contato — o nome vem do lote do `cadastro` com o
     JWT de quem olha (AD-25). Quem não o vê (só pela Tarefa aberta) fica com o código. */
  const semEmpresa = ficha !== undefined && ficha.empresa_id === null
  const pessoaDoNegocio = ficha?.pessoa_id ?? null
  const idsDoContato = useMemo(
    () => (pessoaDoNegocio === null ? [] : [pessoaDoNegocio]),
    [pessoaDoNegocio],
  )
  const contatos = useNomesDePessoas(idsDoContato)
  const nomeDoContato = pessoaDoNegocio === null ? undefined : contatos.nomes.get(pessoaDoNegocio)
  /*
   * O ex-N0 cujo contato ainda não está na Empresa definida (code review da 13.20b2, A1): a espera
   * nasce **dos dados**, e não de um estado desta abertura — fechada e reaberta, a ficha continua
   * dizendo "vinculando…" (e, esgotado o teto, a saída) até o `cadastro` vincular o contato (ou para
   * sempre, na DW-586). Só para o dono (a Responsável e a gestão): quem lê pela Tarefa aberta não vê
   * o contato, e a ausência dele não é atraso.
   */
  const empresaDoExN0 =
    ficha !== undefined && ficha.empresa_id !== null && ficha.pessoa_id !== null
      ? ficha.empresa_id
      : null
  const contatoNaEmpresa =
    pessoaDoNegocio !== null &&
    empresaDoExN0 !== null &&
    (contatos.empresas.get(pessoaDoNegocio)?.includes(empresaDoExN0) ?? false)
  const aguardandoOVinculo =
    empresaDoExN0 !== null &&
    ficha?.quem_le_e_o_dono === true &&
    !contatos.carregando &&
    !contatoNaEmpresa
  const esperaDoVinculo = useEsperaDoVinculo(
    aguardandoOVinculo ? pessoaDoNegocio : null,
    empresaDoExN0,
  )
  /* A espera vista nesta abertura: quando o contato chega, a mesma região diz que chegou. */
  const [esperouOVinculo, setEsperouOVinculo] = useState(false)
  if (aguardandoOVinculo && !esperouOVinculo) {
    setEsperouOVinculo(true)
  }
  /* O foco depois de definir vai ao cartão Empresa (o botão que o abriu some com a definição). */
  const cartaoDaEmpresaRef = useRef<HTMLElement>(null)
  const focarOCartaoDaEmpresa = useRef(false)
  const equipe = useResponsaveis()
  const opcoes = useOpcoesDeDominio()
  const definicoes = camposDoNegocio.useCampos()
  /* A Previsão (11.8): o catálogo ativo e, por `id`, o item excluído que este negócio ainda aponta
     (AD-25 — rótulo, cor e descrição vêm do `negocios`, e não da ficha). */
  const previsaoGravada = ficha?.previsao_de_fechamento_id ?? null

  const compositor = useRef<ControleDoCompositor>(null)
  const [rascunhoNoCompositor, setRascunhoNoCompositor] = useState(false)

  /* O rascunho dos Dados: `null` é "ninguém digitou" — a tela mostra o que o serviço mandou. */
  const [alterado, setAlterado] = useState<{
    /** O id do item de Previsão escolhido; `''` é "nenhuma". */
    previsao: string
    campos: RascunhoDosCampos
  } | null>(null)
  const [salvo, setSalvo] = useState(false)
  /* A gravada e a do rascunho: as duas precisam de rótulo, mesmo que saiam da lista ativa. */
  const previsaoDoRascunho = alterado === null || alterado.previsao === '' ? null : alterado.previsao
  const idsDePrevisao = useMemo(
    () => [previsaoGravada, previsaoDoRascunho],
    [previsaoGravada, previsaoDoRascunho],
  )
  const previsoes = useItensDePrevisao(idsDePrevisao)

  const [notaEmEdicao, setNotaEmEdicao] = useState(false)
  /* 13.14c: o rascunho dos Dados só conta enquanto os campos estão na tela — a ficha relida que
     vira só leitura (um handoff com o modal aberto) não deixa o "Há alteração não salva" preso. */
  const somenteLeitura = ficha?.somente_leitura === true
  /* E o rascunho sai junto: voltando a escrever, a ficha não ressuscita um texto de antes. */
  if (somenteLeitura && alterado !== null) {
    setAlterado(null)
  }
  const pendente = (alterado !== null && !somenteLeitura) || rascunhoNoCompositor || notaEmEdicao
  useEffect(() => {
    aoMudarPendencia?.(pendente)
  }, [pendente, aoMudarPendencia])

  const [dialogo, setDialogo] = useState<'ganho' | 'perda' | 'exclusao' | null>(null)
  /* 13.16: a releitura da ficha ao abrir a janela de Ganho está em voo — "Confirmar ganho" espera
     por ela, para não ganhar sobre um "sem orçamento" que o espelho recém-chegado desmentiria. */
  const [relendoParaOGanho, setRelendoParaOGanho] = useState(false)
  /* 13.5: o diálogo "Trocar a empresa do negócio" aberto. */
  const [trocandoEmpresa, setTrocandoEmpresa] = useState(false)
  /* 13.20b2 (herança da 13.20b1): o Negócio sem Empresa não passa pela troca, e o estado aberto
     não fica preso sem o modal — a ficha que chega sem Empresa com ele ligado o desliga. */
  if (trocandoEmpresa && semEmpresa) {
    setTrocandoEmpresa(false)
  }
  /* 13.20b2: o diálogo "Definir a empresa do negócio" aberto. */
  const [definindoEmpresa, setDefinindoEmpresa] = useState(false)
  /* Depois de definir, o foco vai ao cartão Empresa — no efeito, depois de o diálogo desmontar e
     devolver o foco a quem o abriu (o botão que sumiu com a definição). */
  useEffect(() => {
    if (focarOCartaoDaEmpresa.current && !definindoEmpresa) {
      focarOCartaoDaEmpresa.current = false
      cartaoDaEmpresaRef.current?.focus()
    }
  })
  const [motivoEscolhido, setMotivoEscolhido] = useState<string | null>(null)
  const [descricaoDaPerda, setDescricaoDaPerda] = useState('')
  const gatilhoDoDialogo = useRef<HTMLElement | null>(null)

  const abrirDialogo = (qual: 'ganho' | 'perda' | 'exclusao', gatilho: HTMLElement) => {
    gatilhoDoDialogo.current = gatilho
    setDialogo(qual)
    /* 13.16: o Orçamento espelho chega por evento — relida agora, a janela de Ganho sabe se ele
       já chegou antes de avisar que não há. */
    if (qual === 'ganho') {
      /* Sem `throwOnError`: a releitura que falha não derruba a ficha nem fecha a janela — o que
         estava na tela continua valendo, e o serviço recusa o que precisar recusar. */
      setRelendoParaOGanho(true)
      void consulta.refetch().finally(() => {
        setRelendoParaOGanho(false)
      })
    }
  }

  const devolverOFoco = () => {
    const gatilho = gatilhoDoDialogo.current
    gatilhoDoDialogo.current = null
    gatilho?.focus()
  }

  const funilDaFicha = useMemo(
    () => funis.find((umFunil) => umFunil.id === ficha?.funil_id),
    [funis, ficha],
  )

  const etapasDoCatalogo = useMemo(() => {
    const mapa = new Map<string, { nome: string; funil: Funil }>()
    for (const umFunil of funis) {
      for (const umaEtapa of umFunil.etapas) {
        mapa.set(umaEtapa.id, { nome: umaEtapa.nome, funil: umFunil })
      }
    }
    return mapa
  }, [funis])

  const nomesDaEquipe = useMemo(
    () => new Map((equipe.data ?? []).map((pessoa) => [pessoa.id, pessoa.nome])),
    [equipe.data],
  )

  const nomeDaPessoa = useCallback(
    (id: string | null): string => {
      if (id === null) {
        return VAZIO
      }
      if (id === sessao?.usuario_id) {
        return sessao.nome
      }
      const nome = nomesDaEquipe.get(id)
      if (nome !== undefined) {
        return nome
      }
      return equipe.isError ? EQUIPE_INDISPONIVEL : VAZIO
    },
    [nomesDaEquipe, equipe.isError, sessao],
  )

  const origens = useMemo(() => opcoes.data?.origem_do_cliente ?? [], [opcoes.data])
  const origemDoCliente = empresa.data?.origem_id ?? null
  const motivos = useMemo(() => opcoes.data?.motivo_de_perda ?? [], [opcoes.data])

  const idsAvulsos = useMemo(() => {
    const pendentes: string[] = []
    if (origemDoCliente !== null && !origens.some((opcao) => opcao.id === origemDoCliente)) {
      pendentes.push(origemDoCliente)
    }
    if (
      ficha?.motivo_perda_id != null &&
      !motivos.some((opcao) => opcao.id === ficha.motivo_perda_id)
    ) {
      pendentes.push(ficha.motivo_perda_id)
    }
    return pendentes
  }, [ficha, origemDoCliente, origens, motivos])
  const rotulosAvulsos = useRotulosDeOpcoes(idsAvulsos)

  const rotuloDaOpcao = (id: string | null): string => {
    if (id === null) {
      return VAZIO
    }
    const doVocabulario = [...origens, ...motivos].find((opcao) => opcao.id === id)
    return doVocabulario?.rotulo ?? rotulosAvulsos.get(id) ?? OPCAO_FORA_DA_LISTA
  }

  //: 13.7: o título é o nome exibido — o nome fantasia, e a razão social sem ele.
  const nomeDaEmpresa = empresa.data === undefined ? undefined : nomeExibidoDaEmpresa(empresa.data)
  const razaoSocialAbaixo =
    empresa.data === undefined
      ? null
      : razaoSocialQueDifere(empresa.data.razao_social, empresa.data.nome_fantasia)
  /* O `cadastro` responde `404`/`403` à Empresa que quem lê não vê (FR-30): isso é
     invisibilidade; qualquer outra falha é falha, e não afirma nada sobre a Empresa. */
  const empresaInvisivel =
    empresa.error instanceof ErroDaApi && [403, 404].includes(empresa.error.status)
  /* O nome no título: resolvido, ainda vindo (aria-busy + aviso ao leitor) ou ausente. No Negócio
     sem Empresa (13.20b2) o nome é o do contato, e o "ainda vindo" é o do lote de Pessoas — o
     `useEmpresa` desligado fica `isPending` para sempre, e não pode prender o título. */
  const origemDoNome: 'resolvido' | 'pendente' | 'ausente' = semEmpresa
    ? nomeDoContato !== undefined
      ? 'resolvido'
      : pessoaDoNegocio !== null && contatos.carregando
        ? 'pendente'
        : 'ausente'
    : nomeDaEmpresa !== undefined
      ? 'resolvido'
      : empresa.isPending
        ? 'pendente'
        : 'ausente'
  const titulo =
    (semEmpresa ? nomeDoContato : nomeDaEmpresa) ??
    (ficha === undefined ? TITULO_DA_FICHA : `Negócio ${String(ficha.codigo)}`)

  const movimentacoes = useMemo(
    () =>
      (trilha.data ?? []).map((linha: MovimentacaoDoNegocio) => {
        /* Três formas desde a 11.5 (AD-20): mudança de etapa (responsável nulo), troca pura de
           Responsável (etapa e funil nulos) e handoff — as duas coisas numa linha só. */
        const partes: string[] = []
        if (linha.de_etapa_id !== null && linha.para_etapa_id !== null) {
          const de = etapasDoCatalogo.get(linha.de_etapa_id)?.nome ?? VAZIO
          const para = etapasDoCatalogo.get(linha.para_etapa_id)?.nome ?? VAZIO
          partes.push(`${de} → ${para}`)
        }
        if (linha.de_responsavel_id != null && linha.para_responsavel_id != null) {
          partes.push(
            `Responsável: ${nomeDaPessoa(linha.de_responsavel_id)} → ` +
              nomeDaPessoa(linha.para_responsavel_id),
          )
        }
        return {
          id: linha.id,
          quando: DATA_E_HORA.format(new Date(linha.ocorrida_em)),
          titulo: partes.join(' · '),
          detalhe: `Por ${nomeDaPessoa(linha.autor_id)}`,
          handoff: linha.de_funil_id !== null && linha.de_funil_id !== linha.para_funil_id,
        }
      }),
    [trilha.data, etapasDoCatalogo, nomeDaPessoa],
  )

  const emAndamento = ficha?.status === 'em_andamento'
  const podeEditar = sessao !== undefined && 'editar_negocio' in sessao.permissoes
  /* 13.14c (DW-527, D-8): `somente_leitura` (o serviço diz) é quem lê só pela PA-3 — sem editar,
     mover, ganhar, perder, registrar a entrega, nem mexer em Produtos e RAT; a nota explica. */
  const edicaoLiberada = podeEditar && emAndamento && !somenteLeitura
  const razaoDoBloqueio = emAndamento ? undefined : NEGOCIO_FECHADO
  const razaoDaEdicao =
    !emAndamento || somenteLeitura ? undefined : podeEditar ? undefined : SEM_PERMISSAO_DE_EDITAR
  /* 13.14b (DW-438): trocar o Responsável e excluir são do dono — a gestão ou o Responsável
     (`quem_le_e_o_dono` da ficha); quem só vê pelo Grupo ou pela PA-3 lê o Responsável em texto e
     não vê "Excluir negócio". Conveniência: quem recusa é o serviço (o gatilho e a policy). */
  const dono = ficha?.quem_le_e_o_dono === true
  const podeTrocarResponsavel =
    sessao !== undefined && 'trocar_responsavel' in sessao.permissoes && emAndamento && dono
  /* 13.5 (FR-83): da gestão, e em **qualquer** status — o Negócio ganho ou perdido também muda de
     Empresa, e o desfecho não. Esconder o botão é conveniência: quem recusa é a rota (e o banco). */
  const podeTrocarEmpresa =
    sessao !== undefined && 'trocar_empresa_do_negocio' in sessao.permissoes && !semEmpresa
  /* 13.20b2 (13.20b1, A4): definir a Empresa é da Responsável, com `editar_negocio`, e da gestão
     (escopo `todas`), com `trocar_empresa_do_negocio` — em qualquer status. Quem vê o Negócio só
     pela Tarefa aberta não define. Conveniência: quem recusa é o serviço. */
  /* As razões do Negócio sem Empresa no topo, e o `aria-describedby` do Ganho com a mesma regra. */
  const razoesDoSemEmpresa = semEmpresa && emAndamento && !somenteLeitura
  const podeDefinirEmpresa =
    semEmpresa &&
    !somenteLeitura &&
    sessao !== undefined &&
    ((sessao.usuario_id === ficha.responsavel_id && 'editar_negocio' in sessao.permissoes) ||
      (ehDeGestao(sessao.escopo_de_carteira) &&
        'trocar_empresa_do_negocio' in sessao.permissoes))
  const ocupado =
    mover.isPending ||
    desfecho.isPending ||
    editar.isPending ||
    excluir.isPending ||
    trocarResponsavel.isPending

  const fecharDialogo = () => {
    setDialogo(null)
    setMotivoEscolhido(null)
    setDescricaoDaPerda('')
    desfecho.reset()
    excluir.reset()
    mover.reset()
    trocarResponsavel.reset()
    setTrocaAConfirmar(null)
    devolverOFoco()
  }

  /* O cabeçalho do modal (protótipo v2): à esquerda o nome, o código e "Funil · Etapa"; à
     direita Perdido | Ganho (`acoes`, só com a ficha lida) e o ×. */
  const etapaDaFicha = ficha === undefined ? undefined : etapasDoCatalogo.get(ficha.etapa_id)
  const cabecalho = (fechavel: boolean, acoes?: React.ReactNode) => (
    <div className="ficha__topo-do-modal">
      <div className="ficha__identidade-do-modal">
        <div className="ficha__linha-do-titulo">
          <h2
            className="ficha__titulo-do-modal"
            id="modal-do-negocio-titulo"
            data-nome={origemDoNome}
            aria-busy={origemDoNome === 'pendente' || undefined}
          >
            {titulo}
            {origemDoNome === 'pendente' ? (
              <span className="ficha__so-para-leitor">
                {semEmpresa ? CONTATO_A_CAMINHO : NOME_A_CAMINHO}
              </span>
            ) : null}
          </h2>
          {ficha === undefined ? null : (
            <span className="ficha__codigo-do-modal numero">{ficha.codigo}</span>
          )}
        </div>
        {ficha === undefined ? null : (
          <span className="ficha__sub-do-modal">
            {[funilDaFicha?.nome, etapaDaFicha?.nome].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
      {acoes}
      {aoVerNoFunil !== undefined && ficha !== undefined ? (
        <button
          type="button"
          className="ficha__botao ficha__ver-no-funil"
          onClick={() => {
            aoVerNoFunil(ficha.funil_id)
          }}
        >
          Ver no funil
        </button>
      ) : null}
      {fechavel ? (
        <button
          type="button"
          className="ficha__fechar"
          aria-label="Fechar o negócio"
          onClick={aoFechar}
        >
          ×
        </button>
      ) : null}
    </div>
  )

  if (saiuPelaTroca) {
    /* O efeito acima fecha o modal; até lá, nada de "Negócio não encontrado.". */
    return null
  }

  /* Com a ficha já na tela, a releitura que falha (a da janela de Ganho, por exemplo) não a troca
     pela recusa — salvo o `404`: o Negócio que sumiu não continua desenhado. */
  const recusaSemFicha =
    consulta.isError &&
    (ficha === undefined ||
      (consulta.error instanceof ErroDaApi && consulta.error.status === 404))
  if (recusaSemFicha && !foiExcluido) {
    const naoEncontrado =
      consulta.error instanceof ErroDaApi && consulta.error.status === 404
    return (
      <section className="ficha" aria-labelledby="modal-do-negocio-titulo">
        {cabecalho(true)}
        <div className="ficha__recusa">
          <p className="ficha__erro" role="alert">
            {naoEncontrado ? NEGOCIO_NAO_ENCONTRADO : mensagemDe(consulta.error)}
          </p>
          {naoEncontrado ? null : (
            <button
              type="button"
              className="ficha__botao"
              onClick={() => {
                void consulta.refetch()
              }}
            >
              {TENTAR_DE_NOVO}
            </button>
          )}
          <button type="button" className="ficha__botao" onClick={aoFechar}>
            Fechar
          </button>
        </div>
      </section>
    )
  }

  if (ficha === undefined) {
    return (
      <section className="ficha" aria-labelledby="modal-do-negocio-titulo">
        {cabecalho(true)}
        <p className="ficha__aviso" role="status">
          {CARREGANDO}
        </p>
      </section>
    )
  }

  /* SDR: a chave `valor` não veio — e o campo não existe na tela (nem traço). */
  const veDinheiro = 'valor' in ficha
  const { texto: valorNaTela } = textoDoValor(ficha.valor)
  const origemDoValor = fraseDaOrigemDoValor(
    ficha.numero_do_orcamento_vigente,
    ficha.desconto_percentual_do_orcamento_vigente,
  )
  /* O seletor do Responsável (11.5): a equipe ativa, mais o Responsável atual se ele não estiver
     nela (desativado ou fora da lista lida) — senão o seletor mentiria sobre quem é. */
  const escolhiveisParaResponsavel = (equipe.data ?? []).filter((pessoa) => pessoa.ativo)
  const responsavelForaDaLista = !escolhiveisParaResponsavel.some(
    (pessoa) => pessoa.id === ficha.responsavel_id,
  )
  const funilAntesDaTroca = ficha.funil_id
  /* A troca que dispara o handoff (FR-6): negócio num funil de Prospecção e alvo derivado de
     Vendedor. O servidor decide de verdade (pela réplica); aqui é só para marcar e confirmar. */
  const levaAVendas = (perfil: string | undefined): boolean =>
    funilDaFicha?.tipo === 'prospeccao' && perfil === 'vendedor_closer'
  const perfilDe = (id: string) => escolhiveisParaResponsavel.find((p) => p.id === id)?.perfil
  const trocarOResponsavel = (responsavelId: string) => {
    const nomeDoNovo = nomeDaPessoa(responsavelId)
    trocarResponsavel.mutate(responsavelId, {
      onSuccess: (negocio) => {
        setTrocaAConfirmar(null)
        if (negocio.funil_id === funilAntesDaTroca) {
          /* Troca simples: se o negócio sair do alcance (o refetch dá `404`), este é o aviso. */
          setAvisoDeSaida(textoDaEntrega(nomeDoNovo))
          return
        }
        const destino = etapasDoCatalogo.get(negocio.etapa_id)
        const texto =
          destino === undefined
            ? HANDOFF_SEM_NOMES
            : textoDoHandoff(destino.funil.nome, destino.nome)
        /* O handoff: o aviso sai já — e fica na região de fora, que sobrevive ao modal. */
        anunciar(texto)
        setAvisoDeSaida(texto)
      },
    })
  }
  const escolherResponsavel = (responsavelId: string) => {
    if (responsavelId === ficha.responsavel_id) {
      return
    }
    trocarResponsavel.reset()
    if (levaAVendas(perfilDe(responsavelId))) {
      /* Uma seta no seletor não entrega o negócio a Vendas sozinha: pede confirmação. */
      setTrocaAConfirmar(responsavelId)
      return
    }
    trocarOResponsavel(responsavelId)
  }
  const equipePronta = equipe.isSuccess

  const valoresDosCampos = ficha.campos_personalizados
  const rascunho = alterado ?? {
    previsao: previsaoGravada ?? '',
    campos: Object.fromEntries(
      (definicoes.data ?? []).map((campo) => [campo.id, valorNoControle(valoresDosCampos[campo.id])]),
    ),
  }

  const trocar = (mudanca: Partial<typeof rascunho>) => {
    setAlterado({ ...rascunho, ...mudanca })
    setSalvo(false)
    editar.reset()
  }

  const enviarAEdicao = () => {
    const campos: Record<string, string | number | null> = {}
    for (const campo of definicoes.data ?? []) {
      const novo = rascunho.campos[campo.id] ?? ''
      const atual = valorNoControle(valoresDosCampos[campo.id])
      if (novo === atual) {
        continue
      }
      campos[campo.id] =
        novo.trim() === '' ? null : campo.tipo === 'numero' ? Number(novo) : novo.trim()
    }
    editar.mutate(
      {
        /* Corpo completo (11.8): sempre vai — ausente, o serviço desmarcaria a previsão. */
        previsao_de_fechamento_id: rascunho.previsao === '' ? null : rascunho.previsao,
        ...(Object.keys(campos).length === 0 ? {} : { campos_personalizados: campos }),
      },
      {
        onSuccess: () => {
          setAlterado(null)
          setSalvo(true)
        },
      },
    )
  }

  /* O seletor oferece "Nenhuma" e os ativos — mais todo id do estado que a lista ativa não traz (a
     gravada que a gestão excluiu depois, ou a do rascunho que saiu da lista entre a escolha e o
     salvar): o `<select>` nunca mostra "Nenhuma" com outro id no estado, e manter não é escolher. */
  const previsaoNoRascunho = rascunho.previsao === '' ? null : rascunho.previsao
  const idsForaDaLista = [...new Set([previsaoGravada, previsaoNoRascunho])].filter(
    (id): id is string => id !== null && !previsoes.ativos.some((item) => item.id === id),
  )
  const rotuloForaDaLista = (id: string): string => {
    const item = previsoes.porId.get(id)
    if (item === undefined) {
      return previsoes.falharam.has(id) || previsoes.catalogoFalhou
        ? PREVISAO_SEM_ROTULO
        : PREVISAO_CARREGANDO
    }
    return item.ativo ? item.rotulo : `${item.rotulo}${MARCA_DE_PREVISAO_EXCLUIDA}`
  }
  const marcaDaPrevisao = (id: string | null) => {
    if (id === null) {
      return (
        <span className="ficha__linha-valor" data-vazio="true">
          {VAZIO}
        </span>
      )
    }
    const item = previsoes.porId.get(id)
    if (item !== undefined) {
      return <MarcaDePrevisao item={item} classe="ficha__linha-valor ficha__previsao" comDescricao />
    }
    const falhou = previsoes.falharam.has(id) || previsoes.catalogoFalhou
    return (
      <span
        className="ficha__linha-valor"
        role={falhou ? undefined : 'status'}
        aria-busy={falhou ? undefined : true}
      >
        {falhou ? PREVISAO_SEM_ROTULO : PREVISAO_CARREGANDO}
      </span>
    )
  }
  /* A falha é dita e tem saída de verdade: o botão refaz a leitura do catálogo e dos itens. */
  const avisoDasPrevisoes = previsoes.falhou ? (
    <div className="ficha__recusa">
      <p className="ficha__erro" role="alert">
        {PREVISOES_NAO_VIERAM}
      </p>
      <button type="button" className="ficha__botao" onClick={previsoes.tentarDeNovo}>
        {TENTAR_DE_NOVO}
      </button>
    </div>
  ) : null

  const topo = (
    <section className="ficha__card ficha__desfecho" aria-label="Resumo do negócio">
      {/* O cabeçalho do Negócio (protótipo v2): a Empresa grande, "Funil · responsável" embaixo
          e, à direita, o valor mono e os dias sem contato na cor da régua. */}
      <div className="ficha__cabecalho-topo">
        <div className="ficha__identidade">
          <span className="ficha__empresa" data-nome={origemDoNome}>
            {titulo}
          </span>
          <span className="ficha__card-nota">
            {[funilDaFicha?.nome, `responsável ${nomeDaPessoa(ficha.responsavel_id)}`]
              .filter((parte): parte is string => parte !== undefined)
              .join(' · ')}
          </span>
          {/* O chip de status só quando o Negócio fechou (protótipo v2): em andamento é o normal, e
              não se anuncia — o plano B prova que o Ganho automático não aconteceu pela ausência
              do chip e pelo botão Ganho ainda vivo. */}
          {ficha.status === 'em_andamento' ? null : (
            <span className="ficha__status" data-status={ficha.status}>
              {ROTULO_DO_STATUS[ficha.status]}
            </span>
          )}
        </div>
        <div className="ficha__dinheiro">
          {veDinheiro ? (
            <span className="ficha__valor numero" data-recortado={false}>
              {valorNaTela}
            </span>
          ) : null}
          {/* 13.22b2 (X12): o valor que vem da foto vigente diz de qual — o número e o desconto,
              lidos pela mesma função do valor. As duas chaves não vêm para a SDR. */}
          {veDinheiro && origemDoValor !== null ? (
            <span className="ficha__origem-do-valor">{origemDoValor}</span>
          ) : null}
          <span className="ficha__dias numero" data-cor={ficha.cor}>
            {`${diasPorExtenso(ficha.dias_sem_contato)} sem contato`}
            <span className="ficha__so-para-leitor">{` · ${sinalDaCor(ficha.cor)}`}</span>
          </span>
        </div>
      </div>
      {razaoDoBloqueio === undefined ? null : (
        <p className="ficha__ajuda" id={ID_DA_RAZAO}>
          {razaoDoBloqueio}
        </p>
      )}
      {somenteLeitura ? (
        <p className="ficha__ajuda" id={ID_DA_SO_LEITURA}>
          {SOMENTE_LEITURA_PELA_TAREFA}
        </p>
      ) : null}
      {/* 13.20b2: as recusas `409` do serviço, ditas antes — o Negócio sem Empresa não muda de
          Etapa à mão nem é ganho. Perder, excluir e trocar o Responsável continuam. */}
      {razoesDoSemEmpresa ? (
        <>
          <p className="ficha__ajuda" id={ID_DA_RAZAO_DO_MOVER}>
            {DEFINA_A_EMPRESA_PARA_MOVER}
          </p>
          <p className="ficha__ajuda" id={ID_DA_RAZAO_DO_GANHO}>
            {DEFINA_A_EMPRESA_PARA_GANHAR}
          </p>
          {/* O caminho junto da razão (code review, B6), além do botão do cartão Empresa. */}
          {podeDefinirEmpresa ? (
            <p className="ficha__ajuda">
              <button
                type="button"
                className="ficha__link-de-acao"
                disabled={ocupado}
                onClick={() => {
                  setDefinindoEmpresa(true)
                }}
              >
                {DEFINIR_A_EMPRESA}
              </button>
            </p>
          ) : null}
        </>
      ) : null}
      {catalogo.isError ? (
        <div className="ficha__recusa">
          <p className="ficha__erro" role="alert">
            {mensagemDe(catalogo.error)}
          </p>
          <button
            type="button"
            className="ficha__botao"
            onClick={() => {
              void catalogo.refetch()
            }}
          >
            {TENTAR_DE_NOVO}
          </button>
        </div>
      ) : funilDaFicha === undefined ? (
        <p className="ficha__ajuda" role={catalogo.isLoading ? 'status' : undefined}>
          {catalogo.isLoading ? ETAPAS_A_CAMINHO : FUNIL_FORA_DO_CATALOGO}
        </p>
      ) : (
        <ChevronsDeEtapa
          etapas={funilDaFicha.etapas}
          etapaAtualId={ficha.etapa_id}
          dias={`${String(ficha.dias_sem_contato)}d`}
          desabilitado={!emAndamento || ocupado || somenteLeitura || semEmpresa}
          idDaRazao={
            !emAndamento
              ? ID_DA_RAZAO
              : somenteLeitura
                ? ID_DA_SO_LEITURA
                : semEmpresa
                  ? ID_DA_RAZAO_DO_MOVER
                  : undefined
          }
          aoMover={(etapaId) => {
            mover.mutate(etapaId)
          }}
        />
      )}
      {mover.isError ? (
        <p className="ficha__erro" role="alert">
          {mensagemDe(mover.error)}
        </p>
      ) : null}
    </section>
  )

  const cartaoDeMovimentacoes = (
    <section className="ficha__card ficha__card--solto" aria-label="Movimentações">
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo">Movimentações</span>
      </div>
      <div className="ficha__card-corpo">
        {trilha.isError ? (
          <p className="ficha__erro" role="alert">
            {mensagemDe(trilha.error)}
          </p>
        ) : movimentacoes.length === 0 ? (
          <p className="ficha__vazio">{SEM_MOVIMENTACOES}</p>
        ) : (
          <ul className="ficha__historico" aria-label="Movimentações do negócio">
            {movimentacoes.slice(0, LIMITE_DE_MOVIMENTACOES).map((linha) => (
              <li className="ficha__historico-linha" key={linha.id}>
                <span className="ficha__historico-quando numero">{linha.quando}</span>
                <div className="ficha__historico-corpo">
                  <span className="ficha__historico-titulo">
                    {linha.titulo}
                    {linha.handoff ? (
                      <span className="ficha__marca-de-handoff">{ROTULO_DE_HANDOFF}</span>
                    ) : null}
                  </span>
                  <span className="ficha__historico-detalhe">{linha.detalhe}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
        {movimentacoes.length > LIMITE_DE_MOVIMENTACOES ? (
          <p className="ficha__card-nota ficha__corte">
            {corteDasMovimentacoes(LIMITE_DE_MOVIMENTACOES, movimentacoes.length)}
          </p>
        ) : null}
      </div>
    </section>
  )

  const cartaoDeDados = (
    <section className="ficha__card" aria-label="Dados do negócio">
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo">Dados do negócio</span>
      </div>
      <div className="ficha__card-corpo ficha__campos">
        {podeTrocarResponsavel ? (
          <>
            <Linha rotulo={ROTULOS.responsavel} para={ID_DO_RESPONSAVEL}>
              <select
                id={ID_DO_RESPONSAVEL}
                className="ficha__selecao"
                value={ficha.responsavel_id}
                disabled={ocupado || !equipePronta}
                aria-busy={equipe.isPending || undefined}
                onChange={(evento) => {
                  escolherResponsavel(evento.target.value)
                }}
              >
                {responsavelForaDaLista || !equipePronta ? (
                  <option value={ficha.responsavel_id}>
                    {nomeDaPessoa(ficha.responsavel_id)}
                  </option>
                ) : null}
                {equipePronta
                  ? escolhiveisParaResponsavel.map((pessoa) => (
                      <option key={pessoa.id} value={pessoa.id}>
                        {levaAVendas(pessoa.perfil)
                          ? `${pessoa.nome}${MARCA_DO_HANDOFF_NA_OPCAO}`
                          : pessoa.nome}
                      </option>
                    ))
                  : null}
              </select>
            </Linha>
            {trocarResponsavel.isError ? (
              <p className="ficha__erro" role="alert">
                {mensagemDe(trocarResponsavel.error)}
              </p>
            ) : null}
          </>
        ) : (
          <LinhaLida rotulo={ROTULOS.responsavel} valor={nomeDaPessoa(ficha.responsavel_id)} />
        )}
        <LinhaLida rotulo={ROTULOS.sdr} valor={nomeDaPessoa(ficha.sdr_id)} />
        <LinhaLida rotulo={ROTULOS.inicio} valor={DATA.format(new Date(ficha.data_de_inicio))} />
        {ficha.data_de_conclusao === null ? null : (
          <LinhaLida
            rotulo={ROTULOS.conclusao}
            valor={DATA.format(new Date(ficha.data_de_conclusao))}
          />
        )}
        <LinhaLida rotulo={ROTULOS.origem} valor={rotuloDaOpcao(origemDoCliente)} />
        {ficha.motivo_perda_id === null ? null : (
          <LinhaLida rotulo={ROTULOS.motivo} valor={rotuloDaOpcao(ficha.motivo_perda_id)} />
        )}
        {ficha.descricao_da_perda === null ? null : (
          <LinhaLida rotulo={ROTULOS.descricaoDaPerda} valor={ficha.descricao_da_perda} />
        )}
        {ficha.numero_do_pedido === null ? null : (
          <LinhaLida rotulo={ROTULOS.pedido} valor={ficha.numero_do_pedido} />
        )}
        {ficha.status !== 'ganho' ? null : somenteLeitura ? (
          <LinhaLida rotulo={ROTULOS.entrega} valor={dataSemFuso(ficha.entregue_em)} />
        ) : (
          <LinhaDaEntrega negocioId={ficha.id} entregueEm={ficha.entregue_em} />
        )}

        {edicaoLiberada ? (
          <>
            <Linha rotulo={ROTULOS.previsao} para={ID_DA_PREVISAO}>
              <div className="ficha__previsao-campo">
                {/* Sem o catálogo, o seletor fica travado no que está gravado: salvar os outros
                    campos não desmarca a previsão por acidente. */}
                <select
                  id={ID_DA_PREVISAO}
                  className="ficha__selecao"
                  value={rascunho.previsao}
                  disabled={previsoes.catalogoFalhou}
                  aria-busy={previsoes.carregando || undefined}
                  onChange={(evento) => {
                    trocar({ previsao: evento.target.value })
                  }}
                >
                  <option value="">{SEM_PREVISAO}</option>
                  {idsForaDaLista.map((id) => (
                    <option key={id} value={id}>
                      {rotuloForaDaLista(id)}
                    </option>
                  ))}
                  {previsoes.ativos.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.rotulo}
                    </option>
                  ))}
                </select>
                {previsaoNoRascunho === null ? null : marcaDaPrevisao(previsaoNoRascunho)}
              </div>
            </Linha>
            {avisoDasPrevisoes}
            {(definicoes.data ?? []).map((campo) => {
              const id = `ficha-campo-${campo.id}`
              const valor = rascunho.campos[campo.id] ?? ''
              const aoTrocar = (novo: string) => {
                trocar({ campos: { ...rascunho.campos, [campo.id]: novo } })
              }
              return (
                <Linha key={campo.id} rotulo={campo.nome} para={id}>
                  {campo.tipo === 'lista' ? (
                    <select
                      id={id}
                      className="ficha__selecao"
                      value={valor}
                      onChange={(evento) => {
                        aoTrocar(evento.target.value)
                      }}
                    >
                      <option value="">{VAZIO}</option>
                      {campo.opcoes.map((opcao) => (
                        <option key={opcao} value={opcao}>
                          {opcao}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={id}
                      className="ficha__entrada"
                      type={
                        campo.tipo === 'numero' ? 'number' : campo.tipo === 'data' ? 'date' : 'text'
                      }
                      value={valor}
                      onChange={(evento) => {
                        aoTrocar(evento.target.value)
                      }}
                    />
                  )}
                </Linha>
              )
            })}
            <div className="ficha__acoes">
              <button
                type="button"
                className="ficha__botao ficha__botao--primario"
                disabled={ocupado}
                onClick={enviarAEdicao}
              >
                Salvar dados
              </button>
            </div>
            {editar.isError ? (
              <p className="ficha__erro" role="alert">
                {mensagemDe(editar.error)}
              </p>
            ) : salvo ? (
              <p className="ficha__salvo" role="status">
                {DADOS_GRAVADOS}
              </p>
            ) : null}
          </>
        ) : (
          <>
            <Linha rotulo={ROTULOS.previsao}>{marcaDaPrevisao(previsaoGravada)}</Linha>
            {previsaoGravada === null ? null : avisoDasPrevisoes}
            {(definicoes.data ?? []).map((campo) => (
              <LinhaLida
                key={campo.id}
                rotulo={campo.nome}
                valor={valorNoControle(valoresDosCampos[campo.id]) || VAZIO}
              />
            ))}
            {razaoDaEdicao === undefined ? null : <p className="ficha__ajuda">{razaoDaEdicao}</p>}
          </>
        )}
      </div>
    </section>
  )

  const cartaoDaEmpresa = (
    <section className="ficha__card" aria-label="Empresa" tabIndex={-1} ref={cartaoDaEmpresaRef}>
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo">Empresa</span>
      </div>
      <div className="ficha__card-corpo ficha__campos">
        {semEmpresa ? (
          /* 13.20b2: "Empresa a definir", e não "Carregando a empresa…" (o `useEmpresa` desligado
             fica pendente para sempre). O contato, quando quem olha o vê. */
          <>
            <LinhaLida rotulo="Empresa" valor={EMPRESA_A_DEFINIR} />
            {nomeDoContato === undefined ? null : (
              <LinhaLida rotulo={ROTULO_DO_CONTATO} valor={nomeDoContato} />
            )}
          </>
        ) : empresa.data === undefined ? (
          empresa.isPending ? (
            <p className="ficha__vazio" role="status">
              {EMPRESA_A_CAMINHO}
            </p>
          ) : empresaInvisivel ? (
            <p className="ficha__vazio">{EMPRESA_INVISIVEL}</p>
          ) : (
            /* Falha de rede ou do serviço não é invisibilidade (FR-30): diz o que veio. */
            <div className="ficha__recusa">
              <p className="ficha__erro" role="alert">
                {mensagemDe(empresa.error)}
              </p>
              <button
                type="button"
                className="ficha__botao"
                onClick={() => {
                  void empresa.refetch()
                }}
              >
                {TENTAR_DE_NOVO}
              </button>
            </div>
          )
        ) : (
          <>
            {/* 13.7 (D-9): a Empresa pelo nome exibido, e a razão social logo abaixo quando o nome
                fantasia é outra coisa. */}
            <Linha rotulo="Empresa">
              <Link
                to="/empresas/$empresaId"
                params={{ empresaId: empresa.data.id }}
                className="ficha__link"
              >
                {nomeExibidoDaEmpresa(empresa.data)}
              </Link>
            </Linha>
            {razaoSocialAbaixo === null ? null : (
              <LinhaLida rotulo="Razão social" valor={razaoSocialAbaixo} />
            )}
            <LinhaLida rotulo="CNPJ" valor={empresa.data.documento ?? VAZIO} />
            {/* A única exibição somente-leitura de telefone do cadastro: formatada pela regra da
                máscara (13.2); o livre e o fora do formato aparecem como gravados, e o vazio — nulo,
                só espaço ou só marca invisível (`Cf`) — é o traço. */}
            <LinhaLida
              rotulo="Telefone"
              valor={
                (empresa.data.telefone ?? '').replace(/\p{Cf}/gu, '').trim() === ''
                  ? VAZIO
                  : formatarTelefone(empresa.data.telefone ?? '')
              }
            />
            <LinhaLida rotulo="E-mail" valor={empresa.data.email ?? VAZIO} />
          </>
        )}
        {/* 13.20b2: o contato do ex-N0 — a caminho da Empresa definida, até o `cadastro` o vincular
            pelo evento. Uma região `status` só, sempre montada enquanto há o que dizer, e que troca
            apenas o texto (code review, B6); "Conferir de novo" fica fora dela. */}
        {empresaDoExN0 === null ||
        (!aguardandoOVinculo && nomeDoContato === undefined) ? null : (
          <Linha rotulo={ROTULO_DO_CONTATO}>
            <span className="ficha__linha-valor">
              <span role="status">
                {aguardandoOVinculo
                  ? esperaDoVinculo.estado === 'demorando'
                    ? CONTATO_AINDA_NAO_VINCULADO
                    : VINCULANDO_O_CONTATO
                  : esperouOVinculo
                    ? `${nomeDoContato ?? ''} · ${CONTATO_VINCULADO_A_EMPRESA}`
                    : nomeDoContato}
              </span>
              {aguardandoOVinculo && esperaDoVinculo.estado === 'demorando' ? (
                <>
                  {' '}
                  <button
                    type="button"
                    className="ficha__botao"
                    onClick={esperaDoVinculo.conferirDeNovo}
                  >
                    {CONFERIR_DE_NOVO}
                  </button>
                </>
              ) : null}
            </span>
          </Linha>
        )}
        {podeDefinirEmpresa ? (
          <button
            type="button"
            className="ficha__botao"
            disabled={ocupado}
            onClick={() => {
              setDefinindoEmpresa(true)
            }}
          >
            {DEFINIR_A_EMPRESA}
          </button>
        ) : null}
        {podeTrocarEmpresa ? (
          <button
            type="button"
            className="ficha__botao"
            disabled={ocupado}
            onClick={() => {
              setTrocandoEmpresa(true)
            }}
          >
            {TROCAR_EMPRESA}
          </button>
        ) : null}
      </div>
    </section>
  )

  const dialogoAberto =
    dialogo === 'ganho' ? (
      <Dialogo
        titulo="Marcar negócio como ganho"
        subtitulo={veDinheiro ? `${titulo} · ${valorNaTela}` : titulo}
        rotuloDoConfirmar="Confirmar ganho"
        confirmarDesabilitado={relendoParaOGanho}
        ocupado={desfecho.isPending}
        erro={desfecho.isError ? desfecho.error : undefined}
        aoFechar={fecharDialogo}
        aoConfirmar={() => {
          desfecho.mutate({ status: 'ganho', motivo_perda_id: null }, { onSuccess: fecharDialogo })
        }}
      >
        {avisoDoGanho(ficha) === null ? null : (
          <p className="ficha__dialogo-aviso ficha__dialogo-aviso--sem-orcamento" role="alert">
            {avisoDoGanho(ficha)}
          </p>
        )}
        <p className="ficha__dialogo-aviso">
          Negócio ganho não pode ser reaberto nem excluído. O valor congela no ato.
        </p>
      </Dialogo>
    ) : dialogo === 'perda' ? (
      <Dialogo
        titulo="Marcar negócio como perdido"
        subtitulo={veDinheiro ? `${titulo} · ${valorNaTela}` : titulo}
        rotuloDoConfirmar="Confirmar perda"
        confirmarDesabilitado={motivoEscolhido === null}
        ocupado={desfecho.isPending}
        erro={desfecho.isError ? desfecho.error : undefined}
        aoFechar={fecharDialogo}
        aoConfirmar={() => {
          if (motivoEscolhido === null) {
            return
          }
          const descricao = descricaoDaPerda.trim()
          desfecho.mutate(
            {
              status: 'perdido',
              motivo_perda_id: motivoEscolhido,
              ...(descricao === '' ? {} : { descricao_da_perda: descricao }),
            },
            { onSuccess: fecharDialogo },
          )
        }}
      >
        <div className="ficha__motivos" role="group" aria-label="Motivo da perda — obrigatório">
          <span className="ficha__dialogo-rotulo">Motivo da perda — obrigatório</span>
          {opcoes.isError ? (
            <p className="ficha__erro" role="alert">
              {MOTIVOS_NAO_VIERAM}
            </p>
          ) : opcoes.isPending ? (
            <p className="ficha__vazio" role="status">
              {MOTIVOS_A_CAMINHO}
            </p>
          ) : motivos.length === 0 ? (
            <p className="ficha__vazio">{SEM_MOTIVOS}</p>
          ) : (
            <div className="ficha__chips">
              {motivos.map((opcao) => (
                <button
                  key={opcao.id}
                  type="button"
                  className="ficha__chip"
                  aria-pressed={motivoEscolhido === opcao.id}
                  onClick={() => {
                    setMotivoEscolhido(opcao.id)
                  }}
                >
                  {opcao.rotulo}
                </button>
              ))}
            </div>
          )}
        </div>
        <label className="ficha__dialogo-rotulo" htmlFor="ficha-descricao-da-perda">
          Descrição — opcional
        </label>
        <textarea
          id="ficha-descricao-da-perda"
          className="ficha__entrada ficha__entrada--longa"
          value={descricaoDaPerda}
          maxLength={TAMANHO_DA_DESCRICAO_DA_PERDA}
          onChange={(evento) => {
            setDescricaoDaPerda(evento.target.value)
          }}
        />
        <p className="ficha__dialogo-aviso">
          Negócio perdido não pode ser reaberto nem excluído. Para retomar, cria-se outro.
        </p>
      </Dialogo>
    ) : dialogo === 'exclusao' ? (
      <Dialogo
        titulo="Excluir negócio"
        subtitulo={`${titulo} · Negócio ${String(ficha.codigo)}`}
        rotuloDoConfirmar="Confirmar exclusão"
        ocupado={excluir.isPending}
        erro={excluir.isError ? excluir.error : undefined}
        aoFechar={fecharDialogo}
        aoConfirmar={() => {
          setConfirmouAExclusao(true)
          //: 13.14b (D-4): a Empresa vai junto, para a ficha dela ser relida (o aviso de CNPJ).
          excluir.mutate(ficha.empresa_id ?? undefined, {
            onError: () => {
              setConfirmouAExclusao(false)
            },
            onSuccess: () => {
              setDialogo(null)
              ;(aoExcluido ?? aoFechar)()
            },
          })
        }}
      >
        <p className="ficha__dialogo-aviso">
          A exclusão é real: a linha some do banco, com as notas, as tarefas e o histórico de
          movimentações. Só a trilha de auditoria fica, e ela responde quem excluiu e quando.
        </p>
      </Dialogo>
    ) : null

  /* Perdido | Ganho no topo do modal (protótipo v2, rodada 4: `.status-botoes` no cabeçalho).
     Os glifos são decoração: o nome acessível continua "Perdido" e "Ganho". */
  const acoesDoTopo = (
    <div className="ficha__acoes-de-status">
      <button
        type="button"
        className="ficha__grande ficha__grande--perdido"
        disabled={!emAndamento || ocupado}
        aria-describedby={!emAndamento ? ID_DA_RAZAO : undefined}
        onClick={(evento) => {
          abrirDialogo('perda', evento.currentTarget)
        }}
      >
        <span aria-hidden="true">✗ </span>
        Perdido
      </button>
      <button
        type="button"
        className="ficha__grande ficha__grande--ganho"
        disabled={!emAndamento || ocupado || semEmpresa}
        aria-describedby={
          !emAndamento ? ID_DA_RAZAO : razoesDoSemEmpresa ? ID_DA_RAZAO_DO_GANHO : undefined
        }
        title={emAndamento && semEmpresa ? DEFINA_A_EMPRESA_PARA_GANHAR : undefined}
        onClick={(evento) => {
          abrirDialogo('ganho', evento.currentTarget)
        }}
      >
        <span aria-hidden="true">🏆 </span>
        Ganho
      </button>
    </div>
  )

  return (
    <section className="ficha" aria-labelledby="modal-do-negocio-titulo">
      {cabecalho(true, somenteLeitura ? undefined : acoesDoTopo)}
      {/* Duas colunas (protótipo v2, `.negocio-grade`): à esquerda o que se faz — resumo,
          compositor, histórico, movimentações —; à direita o que se consulta — ID, Orçamento,
          Produtos, Dados, Empresa, E-mails, RAT. A ordem do DOM é a da espinha; no celular as
          colunas empilham nessa mesma ordem. */}
      <div className="ficha__grade">
        <div className="ficha__coluna ficha__coluna--principal">
          {topo}
          <CompositorDoNegocio
            negocioId={negocioId}
            controle={compositor}
            aoMudarRascunho={setRascunhoNoCompositor}
          />
          <HistoricoDeAtividades
            negocioId={negocioId}
            nomeDaPessoa={nomeDaPessoa}
            somenteLeitura={somenteLeitura}
            quemLe={sessao?.usuario_id}
            habilitado={!saindoPelaExclusao}
            aoMudarPendencia={setNotaEmEdicao}
            aoAceitarASugestao={(_tarefa, sugestao) => {
              //: 11.6 (FR-21): Tipo e prazo vêm da sugestão do serviço — a configurada para o
              //: Resultado ou a padrão (mesmo Tipo, 3 dias).
              compositor.current?.preencherComSugestao(
                sugestao.tipo_de_tarefa_id,
                partesDoPrazo(sugestao.prazo).data,
              )
            }}
            aoRecusarASugestao={() => {
              compositor.current?.focar()
            }}
          />
          {cartaoDeMovimentacoes}
        </div>
        <aside className="ficha__coluna ficha__coluna--lateral">
          <IdDoNegocio codigo={ficha.codigo} semEmpresa={semEmpresa && emAndamento} />
          <OrcamentoDoNegocio negocioId={negocioId} codigo={ficha.codigo} />
          <ProdutosDoNegocio
            negocioId={negocioId}
            fechado={!emAndamento}
            somenteLeitura={somenteLeitura}
            excluido={saindoPelaExclusao}
          />
          {/* 13.22a2: a foto do painel de Produtos. Some para a SDR (sem `valor` na ficha) — é
              conveniência: o serviço a recusa com `403` nas três rotas. Code review da 13.22
              (C10): some também para quem lê o Negócio pelo Grupo ou pela tarefa — a ficha diz que
              ele não alcança as fotos, e nada da foto (nem a ida ao ERP) é pedido. */}
          {veDinheiro && ficha.alcanca_o_orcamento_do_crm !== false ? (
            <OrcamentoDoCrm
              negocioId={negocioId}
              responsavelId={ficha.responsavel_id}
              fechado={!emAndamento}
              semEmpresa={semEmpresa}
              somenteLeitura={somenteLeitura}
              excluido={saindoPelaExclusao}
            />
          ) : null}
          {cartaoDeDados}
          {cartaoDaEmpresa}
          <EmailsDoNegocio negocioId={negocioId} />
          <RatDoNegocio
            negocioId={negocioId}
            numeroDoPedido={ficha.numero_do_pedido}
            somenteLeitura={somenteLeitura}
            excluido={saindoPelaExclusao}
          />
        </aside>
      </div>
      {dono ? (
        <div className="ficha__rodape-do-modal">
          <button
            type="button"
            className="ficha__botao ficha__botao--perigo"
            disabled={!emAndamento || ocupado}
            aria-describedby={!emAndamento ? ID_DA_RAZAO : undefined}
            onClick={(evento) => {
              abrirDialogo('exclusao', evento.currentTarget)
            }}
          >
            Excluir negócio
          </button>
        </div>
      ) : null}
      {dialogoAberto}
      {trocandoEmpresa && ficha.empresa_id !== null ? (
        <TrocaDaEmpresaDoNegocio
          negocioId={ficha.id}
          codigo={ficha.codigo}
          empresaAtual={{
            id: ficha.empresa_id,
            nome: nomeDaEmpresa ?? EMPRESA_ATUAL_SEM_NOME,
          }}
          responsavelDoNegocio={ficha.responsavel_id}
          aoFechar={() => {
            setTrocandoEmpresa(false)
          }}
          aoTrocada={(nomeDaEscolhida) => {
            setTrocandoEmpresa(false)
            anunciar(textoDaTroca(nomeDaEscolhida))
          }}
        />
      ) : null}
      {/* Montado só por `definindoEmpresa`, e não também por `semEmpresa`: a releitura da ficha
          (no `onSuccess` da mutação) chega antes do `onSuccess` do `mutate`, e desmontar o diálogo
          ali engolia o `aoDefinida` — e com ele o "vinculando…". Quem o fecha é ele mesmo. */}
      {definindoEmpresa ? (
        <DefinicaoDaEmpresaDoNegocio
          negocioId={ficha.id}
          //: Sem o nome do contato, só o código — e não "Negócio 512 · Negócio 512" (B6).
          subtitulo={
            nomeDoContato === undefined
              ? `Negócio ${String(ficha.codigo)}`
              : `${nomeDoContato} · Negócio ${String(ficha.codigo)}`
          }
          responsavelDoNegocio={ficha.responsavel_id}
          semEmpresa={semEmpresa}
          aoFechar={() => {
            setDefinindoEmpresa(false)
          }}
          aoDefinida={(escolhida) => {
            setDefinindoEmpresa(false)
            focarOCartaoDaEmpresa.current = true
            anunciar(textoDaDefinicao(escolhida.nome))
          }}
        />
      ) : null}
      {trocaAConfirmar === null ? null : (
        <Dialogo
          titulo="Entregar a Vendas?"
          subtitulo={`${titulo} · Negócio ${String(ficha.codigo)}`}
          rotuloDoConfirmar="Entregar"
          ocupado={trocarResponsavel.isPending}
          erro={trocarResponsavel.isError ? trocarResponsavel.error : undefined}
          aoFechar={() => {
            setTrocaAConfirmar(null)
            trocarResponsavel.reset()
          }}
          aoConfirmar={() => {
            trocarOResponsavel(trocaAConfirmar)
          }}
        >
          <p className="ficha__dialogo-aviso">
            {textoDaConfirmacao(nomeDaPessoa(trocaAConfirmar))}
          </p>
        </Dialogo>
      )}
    </section>
  )
}
